import { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { SpinningLoader } from 'common-components'
import { LoadingGradient } from 'common-components/LoadingGradient'
import { COLORS } from 'consts/colors'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { getSourceImageProgressId } from 'store/features/aiAvatar/utils'
import { useGenerationProgress } from 'hooks/useGenerationProgress'
import AvatarActions from './actions'
import * as Styled from './styled'

const AvatarPreview: React.FC = () => {
  const avatar = useSelector(aiAvatarSelectors.avatar)
  const isAvatarLoading = useSelector(aiAvatarSelectors.isAvatarLoading)
  const pendingSourceImageId = useSelector(
    aiAvatarSelectors.pendingSourceImageId
  )

  // Simulated percentage for an in-flight Enhance/Resize — the same
  // timeline-style ticker (generationProgressService), keyed by the op's
  // namespaced id. The id exists only once the kickoff API has responded, so
  // early in the op `progress` is still null (the ring renders without a
  // number — it must NOT fall back to the decode spinner, see below).
  // `isGenerating` also resumes a persisted ticker after a reload.
  const { progress } = useGenerationProgress({
    mediaItemId:
      pendingSourceImageId !== null
        ? getSourceImageProgressId(pendingSourceImageId)
        : null,
    isGenerating: isAvatarLoading && pendingSourceImageId !== null
  })

  // Track which url has finished loading. Deriving the loading state from this
  // (rather than a one-shot flag) re-arms the loader every time the selected
  // avatar changes, so we show the loader until the new image decodes instead of
  // briefly keeping the previous avatar on screen.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null)

  const url = avatar?.url
  const isImageLoading = !!url && loadedUrl !== url
  const showLoader = isAvatarLoading || isImageLoading

  // Hold the generation ring through the RESULT image's decode:
  // `isAvatarLoading` drops the moment the socket applies the result, but the
  // new url still has to decode — without this the loader would flip to the
  // decode spinner for the last blink of the op. Armed while the op runs,
  // released once nothing is loading anymore (also covers the error path,
  // where no new image follows and it releases immediately).
  const [isGenerationSettling, setIsGenerationSettling] = useState(false)
  useEffect(() => {
    if (isAvatarLoading) {
      setIsGenerationSettling(true)
    } else if (!isImageLoading) {
      setIsGenerationSettling(false)
    }
  }, [isAvatarLoading, isImageLoading])

  const showGenerationRing = isAvatarLoading || isGenerationSettling

  // The ring must never render without a number. The ticker's `progress` is
  // null at the op's edges: before the kickoff API returns the imageId that
  // keys the ticker (op running → show 0%), and after complete() has stopped
  // the ticker while the result image decodes (op done → hold 100%, which is
  // continuous with the 100% the ticker itself shows on completion).
  const displayProgress = progress ?? (isAvatarLoading ? 0 : 100)

  return (
    <Styled.PreviewContainer data-testid="avatar-preview">
      {url && (
        <Styled.PreviewImage
          src={url}
          alt="Avatar"
          onLoad={() => setLoadedUrl(url ?? null)}
          onError={() => setLoadedUrl(url ?? null)}
        />
      )}

      {!showLoader && <AvatarActions />}

      {showLoader && (
        <Styled.LoadingOverlay $hasImage={!!url}>
          {showGenerationRing ? (
            // Generation (Enhance/Resize): timeline-style ring for the WHOLE
            // op — from kickoff (before the op's imageId, and thus the
            // ticker, exists — the ring may briefly have no number) through
            // the result image's decode (see isGenerationSettling). Gating
            // on the imageId or dropping at apply-time would flash the
            // decode spinner around the op's edges.
            <LoadingGradient progress={displayProgress} size="small" />
          ) : (
            // Plain image decode (new upload / avatar switch): simple spinner.
            <SpinningLoader
              size={27}
              wrapperSize={32}
              loaderColor={COLORS.primaryDark}
              strokeWidth={2}
            />
          )}
        </Styled.LoadingOverlay>
      )}
    </Styled.PreviewContainer>
  )
}

export default memo(AvatarPreview)
